import { useMemo, useState } from 'react';
import { Button, Popover, Spinner } from '@skupreme/shared/ui';
import { MuteText } from '@elements/Common';
import { DangerButton } from '@elements/Button';
import { ItemImage } from '@components/CatalogElements';
import { WarningIcon } from '@phosphor-icons/react';
import { CommonUtility } from '@utility/common';
import { IconSizes } from '@utility/constants/style-constants';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { BoldText } from './styles';
import { ChangeQuantity } from './ChangeQuantity';

export function ProductRow({
  item,
  qty,
  price,
  canChangeQty,
  qtyChanged,
  hideImage = false,
  isInternational = false,
  currency,
}) {
  const qtyNos = item?.qty;
  const title =
    item.g_attributes?.short_title || item?.p_title || item?.item_name;

  const [popupOpen, setPopupOpen] = useState(false);

  const closePopup = () => {
    setPopupOpen(false);
  };

  const openPopup = () => {
    setPopupOpen(true);
  };

  const isHSCodeError = useMemo(() => {
    if (!isInternational) {
      return false;
    }

    return !item?.hts_code;
  }, [item, isInternational]);

  const redirectToHSCode = () => {
    window.open(
      `/app/catalogs/classification-guide/?search=${encodeURI(item?.p_sku)}`,
      '_blank',
    );
  };

  return (
    <div className="sk:my-3 sk:flex sk:w-full sk:items-center sk:gap-2.5 sk:text-xs">
      <div className="sk:size-[30px] sk:shrink-0">
        {!hideImage && <ItemImage item={item} />}
      </div>
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col">
        <p className="sk:m-0" title={title}>
          <TextCopyToClipboard text={title} iconSize={16} />
        </p>
        {item?.p_sku && (
          <div>
            <MuteText>SKU</MuteText>
            <span className="sk:ml-1">{item.p_sku}</span>
          </div>
        )}
        {(item?.alias || item.loading) && (
          <div className="sk:flex sk:items-center">
            <MuteText>Alias</MuteText>
            {item.loading ? (
              <Spinner size="sm" className="sk:ml-1" />
            ) : (
              <span className="sk:ml-1">{item.alias}</span>
            )}
          </div>
        )}
        {isHSCodeError && (
          <DangerButton
            type="button"
            onClick={redirectToHSCode}
            className="flex-btn sk:w-fit sk:p-2!"
            size="tiny"
          >
            <WarningIcon size={IconSizes.small} className="sk:mr-1" />
            <span>Add HS Code</span>
          </DangerButton>
        )}
        {(qtyNos || qty) && (
          <div className="sk:flex sk:items-center sk:gap-2">
            <div className={item?.p_sku ? '' : 'sk:mt-2'}>
              <MuteText>Qty</MuteText>
              <span className="sk:mx-1">{qty || qtyNos}x</span>
            </div>
            {canChangeQty && (
              <Popover
                trigger={
                  <Button variant="ghost" size="sm" className="sk:underline">
                    Change
                  </Button>
                }
                placement="top"
                aria-label="Shipping quantity"
                isOpen={popupOpen}
                onOpenChange={isOpen => (isOpen ? openPopup() : closePopup())}
              >
                <ChangeQuantity
                  qty={item.newQty || item.qty - item.fulfilledQty}
                  maxQty={item.qty - item.fulfilledQty}
                  qtyChanged={qtyChanged}
                  closePopup={closePopup}
                />
              </Popover>
            )}
          </div>
        )}
        {price && (
          <div>
            Product Total :{' '}
            <BoldText>{CommonUtility.currencyFormat(price, currency)}</BoldText>
          </div>
        )}
      </div>
    </div>
  );
}
